import { gql } from 'apollo-boost';
import useFetchOnButton from 'core/hooks/useFetchOnButton';
import useQuery from 'core/hooks/useQuery';
import { useCallback } from 'react';
import { useGetCourseListQuery } from 'site/graphql';
import useCourseListFilters, { DEFAULT_SKIP, DEFAULT_TAKE } from './useCourseListFilters';

gql`
    query GetCourseList($filter: ProductFormListRequest) {
        productForms(filter: $filter) {
            count
            result {
                id
                type
                price
                title
                maxOrders
                showRemainingOrders
                ordersCount
                paymentStatus
                requiredFieldsNumber
                subscriptionAccess
                showNumberLessons
                order {
                    id
                }
                product {
                    id
                    name
                    shortDescription
                    pictureId
                    offer
                    contentProvider {
                        id
                        offer
                    }
                    lecturers {
                        id
                        account {
                            id
                            firstName
                            lastName
                        }
                    }
                }
                withMeInSetForms {
                    id
                    type
                    subscriptionAccess
                    price
                }
                webinars {
                    id
                    date
                    soonCause
                }
                seminars {
                    id
                    date
                    soonCause
                }
                records {
                    id
                    date
                }
            }
        }
    }
`;

const useCourseListData = () => {
    const filter = useCourseListFilters();

    // Запрос на каждую смену урла
    const { data, loading, fetchMore, refetch } = useGetCourseListQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter },
        notifyOnNetworkStatusChange: true,
    });
    // Формы продукта
    const productForms = (data && data.productForms.result) || [];
    const count = (data && data.productForms.count) || 0;

    // Запрос со следующей страницей
    const fetchMoreQuery = useCallback(
        (take: number, skip: number) => {
            fetchMore({
                variables: { filter: { ...filter, take, skip } },
                updateQuery: (prev, { fetchMoreResult }) => {
                    if (!fetchMoreResult) return prev;
                    return Object.assign({}, prev, {
                        productForms: {
                            ...prev.productForms,
                            count: fetchMoreResult.productForms.count,
                            result: [...prev.productForms.result, ...fetchMoreResult.productForms.result],
                        },
                    });
                },
            });
        },
        [fetchMore, filter],
    );

    // Смена фильтров в урле
    const { changeQuery } = useQuery();
    const fetch = useCallback(
        (newFilter: object) => {
            changeQuery({ ...filter, ...newFilter });
        },
        [changeQuery, filter],
    );

    // Хук для кнопки "Загрузить еще"
    const [isAllItemsDownloaded, loadMore] = useFetchOnButton({
        count,
        defaultSkip: DEFAULT_SKIP,
        defaultTake: DEFAULT_TAKE,
        fetch: fetchMoreQuery,
        loading,
        values: productForms,
    });

    return [isAllItemsDownloaded, loadMore, fetch, productForms, loading, refetch] as const;
};

export default useCourseListData;
